import { CONTACT, useLang } from "@/lib/i18n";
import { IMG } from "@/lib/images";
import { FadeIn, SectionHead } from "@/components/Reveal";
import CtaBand from "@/components/CtaBand";

export default function About() {
  const { t } = useLang();
  const c = t.about;

  return (
    <div>
      <section className="relative overflow-hidden pb-16 pt-16 lg:pt-24" data-testid="about-hero">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(50%_55%_at_80%_0%,rgba(14,124,90,0.08),transparent_70%)]" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn y={14}>
            <div className="flex items-center gap-3">
              <span className="h-px w-8 bg-brand/40" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand">
                {c.eyebrow}
              </span>
            </div>
            <h1 className="mt-6 font-display text-4xl font-semibold leading-[1.15] text-ink sm:text-5xl lg:text-6xl">
              {c.title}
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink2 sm:text-lg">{c.lead}</p>
          </FadeIn>
        </div>
      </section>

      <section className="pb-16 pt-8 lg:py-24" data-testid="about-content">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <FadeIn className="lg:sticky lg:top-28">
              <div className="relative">
                <span
                  aria-hidden
                  className="absolute -bottom-4 -right-4 hidden h-full w-full rounded-none border-2 border-brand/25 sm:block"
                />
                <div className="relative overflow-hidden rounded-none shadow-card">
                  <img
                    src={IMG.facility}
                    alt="Traitement d'équipements mobiles"
                    className="h-[420px] w-full object-cover lg:h-[520px]"
                    loading="lazy"
                  />
                  <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/45 to-transparent" />
                  <span className="absolute bottom-4 left-4 rounded-md bg-white/95 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-ink2">
                    {c.imageTag}
                  </span>
                </div>
              </div>
            </FadeIn>
          </div>

          <div className="lg:col-span-7">
            {c.blocks.map((b, i) => (
              <FadeIn key={i} delay={0.06 * i}>
                <div className={`py-10 ${i > 0 ? "border-t border-line" : "pt-0"}`}>
                  <span className="text-sm font-bold text-brand">{b.num}</span>
                  <h2 className="mt-3 font-display text-2xl font-semibold leading-snug text-ink sm:text-3xl">
                    {b.title}
                  </h2>
                  <p className="mt-4 max-w-xl text-base leading-relaxed text-ink2">{b.body}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-line bg-white py-16 lg:py-24" data-testid="about-facts">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHead eyebrow={c.eyebrow} title={c.title} />
          <FadeIn delay={0.1}>
            <div className="mt-12 grid overflow-hidden rounded-none border border-line sm:grid-cols-3 sm:divide-x sm:divide-line">
              <div className="bg-paper p-8">
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand">
                  {c.facts.regionLabel}
                </p>
                <p className="mt-3 font-display text-lg font-semibold text-ink">{CONTACT.region}</p>
              </div>
              <div className="bg-paper p-8">
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand">
                  {c.facts.legalLabel}
                </p>
                <p className="mt-3 text-sm text-ink2">{CONTACT.siren}</p>
              </div>
              <div className="bg-paper p-8">
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand">
                  {c.facts.emailLabel}
                </p>
                <a
                  href={`mailto:${CONTACT.email}`}
                  data-testid="about-fact-email"
                  className="mt-3 block break-all text-sm text-ink2 transition-colors hover:text-brand"
                >
                  {CONTACT.email}
                </a>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      <CtaBand title={t.home.cta.title} testid="about-cta-band-lot" />
    </div>
  );
}
